import { useMemo } from 'react';

import { ScrapingTab } from './Scraping';
import { Tabs, Tab } from '../../components';
import { ROUTE_QUERY_PARAM } from '../../app/Routes/const';
import { KnowledgeTab } from './Knowledge';
import CompressorTab from './TextCompressorNew';
import { PERMISSIONS } from '../../shared/constants/permissions';
import { PresentationsManagementTab } from './PresentationsManagement';
import {
  filterTabsByAllowedActions,
  type PermissionScopedTab,
  useTabPersistence,
} from '../../shared';
import { usePermissionAllowed } from '../../hooks/usePermissionAllowed';

type TabsEnum = 'knowledge' | 'scraping' | 'compressor' | 'presentations';
const KNOWLEDGE_MANAGEMENT_TABS: (PermissionScopedTab<TabsEnum> & {
  label: string;
})[] = [
  {
    name: 'knowledge',
    label: 'Knowledge',
    action: PERMISSIONS.MANAGE_KNOWLEDGE,
  },
  {
    name: 'scraping',
    label: 'Scraping',
    action: PERMISSIONS.MANAGE_SCRAPER,
  },
  {
    name: 'compressor',
    label: 'Text Compressor',
    action: PERMISSIONS.MANAGE_KNOWLEDGE,
  },
  {
    name: 'presentations',
    label: 'Presentations Management',
    action: PERMISSIONS.MANAGE_PRESENTATIONS,
  },
];

const KnowledgeManagementPage = () => {
  const hasManageKnowledge = usePermissionAllowed([
    PERMISSIONS.MANAGE_KNOWLEDGE,
  ]);
  const hasManageScraper = usePermissionAllowed([PERMISSIONS.MANAGE_SCRAPER]);
  const hasManagePresentations = usePermissionAllowed([
    PERMISSIONS.MANAGE_PRESENTATIONS,
  ]);

  const availableTabs = useMemo<TabsEnum[]>(() => {
    const allowedActions: SpPermission[] = [];
    if (hasManageKnowledge) allowedActions.push(PERMISSIONS.MANAGE_KNOWLEDGE);
    if (hasManageScraper) allowedActions.push(PERMISSIONS.MANAGE_SCRAPER);
    if (hasManagePresentations) {
      allowedActions.push(PERMISSIONS.MANAGE_PRESENTATIONS);
    }

    return filterTabsByAllowedActions(
      KNOWLEDGE_MANAGEMENT_TABS,
      allowedActions
    );
  }, [hasManageKnowledge, hasManageScraper, hasManagePresentations]);

  const tabPersistenceConfig = useMemo(
    () => ({
      paramName: ROUTE_QUERY_PARAM.TAB,
      defaultTab: availableTabs[0] as TabsEnum | undefined,
      updateHistory: true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableTabs[0]]
  );

  const { selectedTab, setSelectedTab, isReady } = useTabPersistence(
    tabPersistenceConfig,
    availableTabs
  );

  if (!isReady) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Tabs<TabsEnum>
        tabs={KNOWLEDGE_MANAGEMENT_TABS}
        activeTab={selectedTab}
        onChange={setSelectedTab}
        contentClassName="mt-md"
      >
        <Tab name="knowledge">
          <KnowledgeTab />
        </Tab>
        <Tab name="scraping">
          <ScrapingTab />
        </Tab>
        <Tab name="compressor">
          <CompressorTab />
        </Tab>
        <Tab name="presentations">
          <PresentationsManagementTab />
        </Tab>
      </Tabs>
    </>
  );
};

export default KnowledgeManagementPage;
